import { useTranslation } from 'react-i18next';
import type { ExecView, GearId, PuzzleType } from '@il-colpo/engine';
import { Badge, Button, Panel } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { PUZZLE_PANELS } from './registry.ts';
import { solverRole } from './shared.tsx';
import styles from './puzzles.module.css';

type Entry = ExecView['puzzles'][number];

/** Gear that works directly on one puzzle, and the role that may use it. */
const GEAR_ON: Partial<Record<PuzzleType, { gear: GearId; role: string }>> = {
  lock: { gear: 'proPick', role: 'locksmith' },
  cameras: { gear: 'jammer', role: 'hacker' },
  panel: { gear: 'emp', role: 'hacker' },
};

export function PuzzleCard({ entry }: { entry: Entry }) {
  const { t } = useTranslation('game');
  const { view, g, send } = useGame();
  const Panel_ = PUZZLE_PANELS[entry.type];
  const exec = view.exec;
  const controller =
    entry.type === 'escape' ? (entry.view['controller'] as typeof view.you.role) : undefined;
  const solver = solverRole(entry.type, controller);
  const mine = solver === null || solver === view.you.role;
  const gear = GEAR_ON[entry.type];
  const canUseGear =
    !entry.solved &&
    gear !== undefined &&
    gear.role === view.you.role &&
    exec?.gear.includes(gear.gear) === true &&
    (entry.type !== 'lock' || entry.view['progress'] === 0);

  return (
    <Panel
      as="article"
      tone={entry.solved ? 'night' : 'lamp'}
      className={[styles['card'], entry.solved ? styles['solved'] : ''].join(' ')}
      aria-label={g(`puzzles.${entry.type}.name`)}
    >
      <header className={styles['cardHead']}>
        <h3 className={styles['cardTitle']}>{g(`puzzles.${entry.type}.name`)}</h3>
        <span className={styles['cardMeta']}>{g(`rooms.${entry.room}`)}</span>
        {entry.solved ? (
          <Badge tone="green">{t('puzzle.solved')}</Badge>
        ) : mine ? (
          <Badge tone="cyan">{t('puzzle.yours')}</Badge>
        ) : (
          <Badge>{g(`roles.${solver ?? 'mastermind'}`)}</Badge>
        )}
      </header>
      {entry.solved ? (
        <p className={styles['muted']}>{t('puzzle.done')}</p>
      ) : (
        <>
          <p className={styles['goal']}>{g(`puzzles.${entry.type}.goal`)}</p>
          <Panel_ puzzleId={entry.id} type={entry.type} v={entry.view} />
          {canUseGear && gear ? (
            <Button
              variant="secondary"
              icon="spark"
              onClick={() => send({ type: 'gear.use', gear: gear.gear, puzzleId: entry.id })}
            >
              {t('puzzle.useGear', { gear: g(`gear.${gear.gear}.name`) })}
            </Button>
          ) : null}
        </>
      )}
    </Panel>
  );
}
